import { createChart, LineSeries } from "lightweight-charts";
import { useEffect, useMemo, useRef, useState } from "react";
import type { OnStateChange } from "@/components/mosaic/ui/TileTitle/TileTitle";
import { Button } from "@/components/ui/button";
import {
	getChartOptions,
	getColorByValue,
} from "@/components/widgets/Revision/lib/helpers";
import {
	AnalystsEstimatesChartTimeFrame,
	AnalystsEstimatesChartViewMode,
	type RevisionExt,
} from "@/components/widgets/Revision/types";
import { sortedByDateAndType } from "@/components/widgets/Revision/utils/RevisionTableUtils";
import { ChartSettingsPanel } from "../../../../ChartSettingsPanel";
import { DEFAULT_AVAILABLE_DATA, ESTIMATES_MODE_NAME } from "../../../config";
import {
	chartData,
	filterData,
	timeFramesForSelection,
	todayData,
} from "../../../lib/helpers";

export const DispersionLightweightChart = ({
	data,
	analystsEstimatesChartTimeFrame,
	analystsEstimatesChartViewMode,
	onStateChange,
}: {
	data: RevisionExt[];
	analystsEstimatesChartTimeFrame: AnalystsEstimatesChartTimeFrame;
	analystsEstimatesChartViewMode: AnalystsEstimatesChartViewMode;
	onStateChange?: OnStateChange;
}) => {
	const [timeFrames, setTimeFrames] = useState<string[]>([]);
	const [timeFrameType, setTimeFrameType] =
		useState<AnalystsEstimatesChartTimeFrame>(analystsEstimatesChartTimeFrame);
	const [estimatesValue, setEstimatesValue] =
		useState<AnalystsEstimatesChartViewMode>(analystsEstimatesChartViewMode);

	const [availableData, setAvailableData] = useState(DEFAULT_AVAILABLE_DATA);

	const chartContainerRef = useRef<HTMLDivElement>(null);

	useEffect(() => {
		const eeData = data
			.map((item) => item?.dp?.ns?.ee)
			.filter((item) => !!item);

		const hasAnnualsConsensus =
			filterData(
				eeData,
				AnalystsEstimatesChartViewMode.DISPERSION,
				AnalystsEstimatesChartTimeFrame.ANNUALS,
			).length > 0;
		const hasQuartersConsensus =
			filterData(
				eeData,
				AnalystsEstimatesChartViewMode.DISPERSION,
				AnalystsEstimatesChartTimeFrame.QUARTERS,
			).length > 0;
		const hasAnnualsStdDev =
			filterData(
				eeData,
				AnalystsEstimatesChartViewMode.STD_DEVIATION,
				AnalystsEstimatesChartTimeFrame.ANNUALS,
			).length > 0;
		const hasQuartersStdDev =
			filterData(
				eeData,
				AnalystsEstimatesChartViewMode.STD_DEVIATION,
				AnalystsEstimatesChartTimeFrame.QUARTERS,
			).length > 0;

		setAvailableData({
			hasAnnualsConsensus,
			hasQuartersConsensus,
			hasAnnualsStdDev,
			hasQuartersStdDev,
		});
	}, [data]);

	// biome-ignore lint/correctness/useExhaustiveDependencies: <We don't need to react on function changes>
	useEffect(() => {
		setTimeFrames(displayedTimeFrames);
	}, [data, timeFrameType]);

	const displayedTimeFrames = useMemo(() => {
		return timeFramesForSelection(
			timeFrameType,
			sortedByDateAndType(todayData(data)?.dp?.ns?.ee) ?? [],
		);
	}, [timeFrameType, data]);

	// biome-ignore lint/correctness/useExhaustiveDependencies: <We don't need to react on function changes>
	useEffect(() => {
		if (!chartContainerRef.current) return;

		const handleResize = () => {
			if (chartContainerRef.current?.clientWidth) {
				chart.applyOptions({
					width: chartContainerRef.current.clientWidth,
					height: chartContainerRef.current.clientHeight,
				});
			}
		};

		const chart = createChart(
			chartContainerRef.current,
			getChartOptions(chartContainerRef),
		);

		chart.timeScale().fitContent();
		if (timeFrames?.length) {
			timeFrames.map((value) => {
				const series = chart.addSeries(LineSeries, {
					color: getColorByValue(value, displayedTimeFrames),
					lineWidth: 1,
				});
				series.applyOptions({
					lastValueVisible: false,
					priceLineVisible: false,
				});
				series.setData(chartData(data, estimatesValue, value));
			});
		}

		window.addEventListener("resize", handleResize);

		return () => {
			window.removeEventListener("resize", handleResize);

			chart.remove();
		};
	}, [
		data,
		timeFrames,
		timeFrameType,
		estimatesValue,
		chartContainerRef?.current?.clientWidth,
		chartContainerRef?.current?.clientHeight,
	]);

	const handleToggle = () => {
		const next =
			timeFrameType === AnalystsEstimatesChartTimeFrame.ANNUALS
				? AnalystsEstimatesChartTimeFrame.QUARTERS
				: AnalystsEstimatesChartTimeFrame.ANNUALS;

		setTimeFrameType(next);
		onStateChange?.({ analystsEstimatesChartTimeFrame: next });
	};

	const handleEstimatesValue = () => {
		const next =
			estimatesValue === AnalystsEstimatesChartViewMode.DISPERSION
				? AnalystsEstimatesChartViewMode.STD_DEVIATION
				: AnalystsEstimatesChartViewMode.DISPERSION;

		setEstimatesValue(next);
		onStateChange?.({ analystsEstimatesChartViewMode: next });
	};

	const isDisabledMode =
		(availableData.hasAnnualsConsensus || availableData.hasQuartersConsensus) &&
		(availableData.hasAnnualsStdDev || availableData.hasQuartersStdDev);
	const isDisabledTileFrame =
		(availableData.hasAnnualsConsensus || availableData.hasAnnualsStdDev) &&
		(availableData.hasQuartersConsensus || availableData.hasQuartersStdDev);

	return (
		<>
			<div className="flex justify-between bg-background">
				<ChartSettingsPanel
					isDisabledButton={!isDisabledTileFrame}
					switchLabel={
						timeFrameType === AnalystsEstimatesChartTimeFrame.ANNUALS
							? "Annuals"
							: "Quarters"
					}
					handleSwitch={handleToggle}
					toggles={displayedTimeFrames}
					selectedToggles={timeFrames}
					setSelectedToggles={setTimeFrames}
				/>
				<Button
					className="p-0 m-0 text-base text-wrap text-gold hover:text-gold font-bold cursor-pointer h-auto bg-background"
					onClick={handleEstimatesValue}
					value={estimatesValue}
					disabled={!isDisabledMode}
				>
					{ESTIMATES_MODE_NAME[estimatesValue]}
				</Button>
			</div>
			<div
				className="w-full aspect-[1.618]"
				ref={chartContainerRef}
				data-testid="chart-container"
			/>
		</>
	);
};
